import { useBloc } from "@blac/react";
import React, { useEffect, useId, useRef } from "react";
import WheelPickerBloc from "./WheelPickerBloc";
import { WheelPickerContextProvider } from "./WheelPickerContext";
import { Wrap } from "./WheelPickerStyled";

export type WheelPickerFieldItem = {
  label: string;
  value: string;
};

export type WheelPickerField = {
  name: string;
  items: WheelPickerFieldItem[];
  value: string;
  onChange?: (value: string) => void;
  loop?: boolean;
  alignContent?: "left" | "right" | "center";
  width?: string;
  style?: React.CSSProperties;
};

export default function WheelPicker<T>({
  children,
  onChange,
  pickerId,
  initialValue,
  parseValues
}: {
  children: React.ReactNode;
  onChange?: (data: Record<string, string>) => void;
  pickerId?: string;
  initialValue?: T;
  parseValues?: (itemValues: Record<string, string>) => T | null;
}) {
  const uid = useId();
  const contextId = pickerId ?? uid;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [, { setEmblaRef }] = useBloc(WheelPickerBloc<T>, {
    id: contextId,
    props: {
      onChange,
      initialValue,
      parseValues
    }
  });

  useEffect(() => {
    if (wrapRef.current) {
      setEmblaRef(wrapRef.current);
    }
  }, [wrapRef.current]);

  // A scrollable modal ancestor otherwise lets the browser hand vertical
  // touchmoves to the compositor fast-scroll path, which delivers them as
  // non-cancelable — and embla aborts any drag on a non-cancelable move. A
  // single non-passive touchmove listener on document opts the gesture out of
  // that fast path so the events stay cancelable and embla can drag. Scoped to
  // the picker's lifetime and removed on unmount.
  useEffect(() => {
    const keepCancelable = () => undefined;
    document.addEventListener("touchmove", keepCancelable, {
      passive: false,
      capture: true
    });
    return () => {
      document.removeEventListener("touchmove", keepCancelable, {
        capture: true
      });
    };
  }, []);

  return (
    <WheelPickerContextProvider value={{ id: contextId }}>
      <Wrap className="embla" ref={wrapRef}>
        {children}
      </Wrap>
    </WheelPickerContextProvider>
  );
}
